import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, Send, X } from "lucide-react";
import AshokaChakra from "./AshokaChakra";
import SearchModal from "./SearchModal";
import { useDismissibleBanner } from "../lib/upvotes";

const NAV_ITEMS = [
    { to: "/", label: "Home" },
    { to: "/categories", label: "Categories" },
    { to: "/opportunities", label: "Opportunities" },
    { to: "/latest", label: "Latest" },
    { to: "/about", label: "About" },
];

export default function Layout({ children }) {
    const [searchOpen, setSearchOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const banner = useDismissibleBanner("submit-cta-v1");
    const location = useLocation();
    const navigate = useNavigate();

    // Close mobile nav on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // Cmd/Ctrl + K search hotkey
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen(true);
            }
            if (e.key === "/" && document.activeElement === document.body) {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div className="min-h-screen flex flex-col bg-cream text-ink">
            {/* Tricolor stripe */}
            <div className="tricolor-strip" />

            {/* Announcement banner */}
            {banner.open && (
                <div
                    className="bg-saffron text-white text-sm"
                    data-testid="announcement-banner"
                >
                    <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
                        <p className="leading-snug">
                            <span aria-hidden>🇮🇳</span> 202 verified Indian
                            companies across 61 categories —{" "}
                            <Link
                                to="/submit"
                                className="underline underline-offset-2 hover:text-white/90 font-semibold"
                                data-testid="banner-submit-link"
                            >
                                Submit yours →
                            </Link>
                        </p>
                        <button
                            onClick={banner.dismiss}
                            aria-label="Dismiss announcement"
                            className="shrink-0 hover:bg-white/15 rounded p-1"
                            data-testid="banner-dismiss"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Sticky nav */}
            <header
                className="sticky top-0 z-40 backdrop-blur-xl bg-cream/85 border-b border-line"
                data-testid="site-header"
            >
                <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between gap-4">
                    <Link
                        to="/"
                        className="flex items-center gap-2 group"
                        data-testid="logo-link"
                    >
                        <span className="text-saffron group-hover:rotate-12 transition-transform">
                            <AshokaChakra size={28} />
                        </span>
                        <span className="font-heading font-extrabold text-ink text-xl tracking-tight">
                            BharatStack
                        </span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        isActive
                                            ? "text-saffron bg-saffron-50"
                                            : "text-ink hover:text-saffron hover:bg-saffron-50/50"
                                    }`
                                }
                                data-testid={`nav-${item.label.toLowerCase()}`}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSearchOpen(true)}
                            className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-ink-muted border border-line hover:border-saffron/40 hover:text-ink bg-white transition-colors min-w-[220px]"
                            data-testid="search-trigger"
                            aria-label="Open search"
                        >
                            <Search className="h-4 w-4" />
                            <span>Search products...</span>
                            <kbd className="ml-auto text-[10px] font-mono bg-cream border border-line rounded px-1.5 py-0.5">
                                ⌘K
                            </kbd>
                        </button>

                        <button
                            onClick={() => setSearchOpen(true)}
                            className="md:hidden p-2 rounded-lg text-ink hover:bg-saffron-50"
                            aria-label="Search"
                            data-testid="search-trigger-mobile"
                        >
                            <Search className="h-5 w-5" />
                        </button>

                        <Link
                            to="/submit"
                            className="hidden sm:inline-flex btn-saffron items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold"
                            data-testid="submit-product-cta"
                        >
                            <Send className="h-4 w-4" />
                            Submit a Product
                        </Link>

                        <button
                            onClick={() => setMobileOpen((v) => !v)}
                            className="md:hidden p-2 rounded-lg text-ink hover:bg-saffron-50"
                            aria-label="Open menu"
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileOpen ? (
                                <X className="h-5 w-5" />
                            ) : (
                                <Menu className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileOpen && (
                    <div className="md:hidden border-t border-line bg-cream">
                        <nav className="px-4 py-3 flex flex-col gap-1">
                            {NAV_ITEMS.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    className={({ isActive }) =>
                                        `px-3 py-2 rounded-lg text-sm font-medium ${
                                            isActive
                                                ? "text-saffron bg-saffron-50"
                                                : "text-ink hover:bg-saffron-50/50"
                                        }`
                                    }
                                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/submit"
                                className="mt-2 btn-saffron text-center px-4 py-2.5 rounded-lg text-sm font-semibold"
                                data-testid="mobile-submit-cta"
                            >
                                Submit a Product
                            </Link>
                        </nav>
                    </div>
                )}
            </header>

            <main className="flex-1">{children}</main>

            <Footer />

            <SearchModal
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
                onPick={(p) => {
                    setSearchOpen(false);
                    navigate(`/product/${p.slug}`);
                }}
            />
        </div>
    );
}

function Footer() {
    return (
        <footer className="bg-cream border-t border-line mt-24">
            <div className="max-w-7xl mx-auto px-4 lg:px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
                <div>
                    <Link to="/" className="flex items-center gap-2">
                        <span className="text-saffron">
                            <AshokaChakra size={28} />
                        </span>
                        <span className="font-heading font-extrabold text-ink text-xl">
                            BharatStack
                        </span>
                    </Link>
                    <p className="mt-3 text-sm text-ink-muted leading-relaxed max-w-xs">
                        Discover Indian alternatives for digital products &
                        services. Support Indian startups. Keep data in India.
                        Pay in ₹.
                    </p>
                    <p className="mt-4 text-sm text-ink">
                        Made with{" "}
                        <span className="text-saffron font-semibold">♥</span>{" "}
                        in India <span aria-hidden>🇮🇳</span>
                    </p>
                </div>

                <div>
                    <h4 className="font-heading font-bold text-ink mb-3">
                        Navigation
                    </h4>
                    <ul className="space-y-2 text-sm">
                        <li>
                            <Link to="/" className="text-ink-muted hover:text-saffron">
                                Home
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/categories"
                                className="text-ink-muted hover:text-saffron"
                            >
                                All Categories
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/latest"
                                className="text-ink-muted hover:text-saffron"
                            >
                                Latest Additions
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/submit"
                                className="text-ink-muted hover:text-saffron"
                            >
                                Submit a Product
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/about"
                                className="text-ink-muted hover:text-saffron"
                            >
                                About
                            </Link>
                        </li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-heading font-bold text-ink mb-3">
                        Popular Categories
                    </h4>
                    <ul className="space-y-2 text-sm">
                        {[
                            ["payment-service-providers", "Payment Gateways"],
                            ["cloud-computing-platforms", "Cloud Hosting"],
                            ["email-marketing-services", "Email Marketing"],
                            ["video-conferencing-software", "Video Conferencing"],
                            ["generative-ai-chatbots", "AI Chatbots"],
                            ["sms-apis", "SMS APIs"],
                        ].map(([slug, label]) => (
                            <li key={slug}>
                                <Link
                                    to={`/category/${slug}`}
                                    className="text-ink-muted hover:text-saffron"
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 lg:px-6 pb-6 flex items-center justify-between text-xs text-ink-muted">
                <span>© {new Date().getFullYear()} BharatStack</span>
                <span>bharatstack.in</span>
            </div>

            <div className="tricolor-strip-thick" />
        </footer>
    );
}
